import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'msc.login.blockedUntil';

/** Estado do bloqueio por excesso de tentativas. */
export interface LoginBlock {
  /** Segundos restantes. `0` quando não há bloqueio. */
  remainingSeconds: number;
  /** Duração total do bloqueio, em minutos. */
  blockMinutes: number;
  isBlocked: boolean;
}

function readStored(): { until: number; minutes: number } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as { until?: unknown; minutes?: unknown };
    if (typeof parsed.until !== 'number' || typeof parsed.minutes !== 'number') return null;
    if (parsed.until <= Date.now()) return null;

    return { until: parsed.until, minutes: parsed.minutes };
  } catch {
    return null;
  }
}

/**
 * Contagem regressiva do bloqueio de login.
 *
 * O fim do bloqueio é guardado no navegador, então a contagem **sobrevive a um
 * recarregamento**: sem isso, a tela voltaria liberada e o usuário levaria outro
 * 429 sem entender por quê.
 *
 * O armazenamento é só conveniência de exibição — quem decide é o servidor. Um
 * usuário que apague o registro continua bloqueado na próxima tentativa.
 */
export function useLoginBlock(): LoginBlock & {
  start: (retryAfterSeconds: number, blockMinutes: number) => void;
  clear: () => void;
} {
  const [until, setUntil] = useState<number | null>(() => readStored()?.until ?? null);
  const [blockMinutes, setBlockMinutes] = useState(() => readStored()?.minutes ?? 0);
  const [remainingSeconds, setRemainingSeconds] = useState(() => {
    const stored = readStored();
    return stored ? Math.ceil((stored.until - Date.now()) / 1000) : 0;
  });

  const clear = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Sem armazenamento, a contagem simplesmente não sobrevive ao reload.
    }
    setUntil(null);
    setRemainingSeconds(0);
  }, []);

  const start = useCallback((retryAfterSeconds: number, minutes: number) => {
    const target = Date.now() + retryAfterSeconds * 1000;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ until: target, minutes }));
    } catch {
      // idem
    }
    setUntil(target);
    setBlockMinutes(minutes);
    setRemainingSeconds(retryAfterSeconds);
  }, []);

  useEffect(() => {
    if (until === null) return;

    const tick = (): void => {
      const restante = Math.ceil((until - Date.now()) / 1000);
      if (restante <= 0) {
        clear();
        return;
      }
      setRemainingSeconds(restante);
    };

    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [until, clear]);

  return { remainingSeconds, blockMinutes, isBlocked: remainingSeconds > 0, start, clear };
}

/** `325` → `5:25`. */
export function formatCountdown(seconds: number): string {
  const minutos = Math.floor(seconds / 60);
  const restante = seconds % 60;
  return `${minutos}:${String(restante).padStart(2, '0')}`;
}
